import {
  APP_UPDATE_RELOADING_EVENT,
  APP_UPDATE_REQUIRED_EVENT,
} from "./appUpdateEvents.js";

const RECOVERY_KEY = "doitify:runtime-asset-recovery";
const MAX_ATTEMPTS = 2;
const ATTEMPT_WINDOW_MS = 30_000;
const RELOAD_COUNTDOWN_SECONDS = 3;
/** Grace period for React (WebUpdateBanner) to claim the reload event. */
const RELOAD_HANDOFF_TIMEOUT_MS = 500;
const INSTALLATION_KEY = Symbol.for("doitify:runtime-asset-recovery-installed");
let recoveryRequestedAt = null;

export function isRuntimeAssetError(value) {
  const message =
    typeof value === "string"
      ? value
      : value?.payload?.message ||
        value?.payload ||
        value?.reason?.message ||
        value?.reason ||
        value?.message ||
        String(value ?? "");

  return /Loading chunk .* failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    message,
  );
}

function getRecoveryState() {
  try {
    const state = JSON.parse(sessionStorage.getItem(RECOVERY_KEY) || "null");
    if (!state || Date.now() - state.startedAt > ATTEMPT_WINDOW_MS) {
      return { attempts: 0, startedAt: Date.now() };
    }
    return state;
  } catch {
    return { attempts: 0, startedAt: Date.now() };
  }
}

async function clearRuntimeCaches() {
  if (typeof caches !== "undefined") {
    // Do not erase old hashed chunks: other tabs and offline clients need them.
    await caches.delete("pages-cache");
  }

  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(registrations
      .filter((registration) =>
        [registration.active, registration.waiting, registration.installing]
          .some((worker) => worker && new URL(worker.scriptURL).pathname === "/sw.js"),
      )
      .map((registration) =>
        Promise.resolve()
          .then(() => registration.update())
          .catch(() => {
            // A stale/invalid registration rejects with InvalidStateError;
            // recovery must never be blocked by the SW being in a bad state.
          })
      ));
  }
}

/**
 * Last-resort refresh: discard cached navigation HTML and check for a new SW.
 * Preserve content-hashed assets and push registrations used by other tabs.
 */
export async function forceRefreshAndReload() {
  try {
    await clearRuntimeCaches();
  } catch {
    // Reload even when cache cleanup fails.
  }
  window.location.reload();
}

/**
 * Hand the reload over to the React layer (WebUpdateBanner): it shows a
 * "new version deployed, refreshing in Ns" notice with a Cancel button, so
 * users in the middle of a form are never yanked away silently. If no React
 * listener claims the event (app not mounted yet), fall back to the direct
 * reload — a broken page must never just sit there.
 */
function requestCountdownReload() {
  const event = new CustomEvent(APP_UPDATE_RELOADING_EVENT, {
    detail: { countdownSeconds: RELOAD_COUNTDOWN_SECONDS },
    cancelable: true,
  });
  window.dispatchEvent(event);
  if (event.defaultPrevented) return;

  setTimeout(() => {
    if (!event.defaultPrevented) forceRefreshAndReload();
  }, RELOAD_HANDOFF_TIMEOUT_MS);
}

export function recoverFromRuntimeAssetError(value) {
  if (typeof window === "undefined" || !isRuntimeAssetError(value)) return false;
  // A refresh cannot fix an offline connection, and may lose unsaved work.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  // Vite, React's boundary and unhandledrejection may report the same failure.
  const now = Date.now();
  const handoffWindow = RELOAD_COUNTDOWN_SECONDS * 1000 + RELOAD_HANDOFF_TIMEOUT_MS;
  if (recoveryRequestedAt !== null && now - recoveryRequestedAt < handoffWindow) {
    return true;
  }
  recoveryRequestedAt = now;

  const state = getRecoveryState();
  if (state.attempts >= MAX_ATTEMPTS) {
    // Auto-reload failed repeatedly — stop reloading in circles and let the
    // user decide via the update banner.
    window.dispatchEvent(new CustomEvent(APP_UPDATE_REQUIRED_EVENT));
    return true;
  }

  try {
    sessionStorage.setItem(
      RECOVERY_KEY,
      JSON.stringify({ ...state, attempts: state.attempts + 1 }),
    );
  } catch {
    // Continue with the reload when storage is unavailable.
  }

  requestCountdownReload();
  return true;
}

export function installRuntimeAssetRecovery() {
  if (typeof window === "undefined" || window[INSTALLATION_KEY]) return;
  window[INSTALLATION_KEY] = true;

  window.addEventListener("vite:preloadError", (event) => {
    // Vite puts the original import error in `payload`, not `message`.
    // Never swallow an unrelated error or return an undefined lazy module.
    // React must still receive the rejection if the user cancels the refresh.
    recoverFromRuntimeAssetError(event.payload);
  });
  window.addEventListener("unhandledrejection", (event) => {
    recoverFromRuntimeAssetError(event);
  });
  window.addEventListener("error", (event) => {
    recoverFromRuntimeAssetError(event.error || event.message);
  });
}
